import { useTRPC } from "@/trpc"
import { useTypedParams } from 'react-router-typesafe-routes'
import { ROUTES_CONFIG } from "@/config/routes.config"
import {
    Card,
    CardHeader,
    CardFooter,
    CardTitle,
    CardAction,
    CardDescription,
    CardContent,
}
    from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/animated-tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import React, { useState } from "react"
import { Link } from 'react-router-dom';
import * as Icon from 'lucide-react'
import { useQuery } from "@tanstack/react-query"
import { formatDate } from "@/lib/date"
import PlayersTable from "./(components)/players"
import MatchHeader from "./(components)/match-header";
import FormationGrid from "./(components)/formation-field";
import type { Player as FormationPlayer, PlacedPlayer } from "@/lib/formation-placement";
import PlayerFormationCard from "./(components)/player-formation-card";

type SidePlayerRaw = {
  id: string;
  player?: { id: string; name?: string };
  player_impect_id?: string;
  start_position?: string;
  position?: string;
  shirt_number?: number;
  is_starter?: boolean;
  side_id: string;
};

type Side = {
  id: string;
  side: 'HOME' | 'AWAY';
  starting_formation?: string;
  players?: SidePlayerRaw[];
};

type PlayerRow = { id: string; name: string; position: string; shirtNumber: number ; playerId: string; };

type Competition = { id: string; name: string; };

const MatchDetailPage = () => {
    const { trpc } = useTRPC()
    const { match_id } = useTypedParams(ROUTES_CONFIG.APP.MATCH_DETAIL)
    const [tab, setTab] = useState<string>('dashboard');

    const [selectedPlayer, setSelectedPlayer] = React.useState<PlacedPlayer | undefined>(undefined);

    const { data: match } = useQuery({
        queryKey: ['match', match_id],
        queryFn: () => trpc.matches.getById.query({ id: match_id }),
        enabled: !!match_id,
    });

    const competition: Competition = {
        id: match?.competitionId ?? "",
        name: match?.competitionName ?? "Onbekend",
    };

    const toFormationPlayers = (raw?: SidePlayerRaw[]): FormationPlayer[] =>
    (raw ?? [])
        .filter((p) => p.is_starter)
        .map((p) => ({
        id: p.id,
        playerId: p.player?.id ?? "",
        name: p.player?.name ?? "Onbekend",
        playerImpectId: p.player_impect_id ?? "",
        shirtNo: p.shirt_number ?? null,
        positionRaw: p.start_position ?? p.position ?? "",
        side_id: p.side_id,
    }));

    const toPlayersTableRows = (raw?: SidePlayerRaw[]): PlayerRow[] =>
        (raw ?? []).map((p) => ({
            id: p.id,
            name: p.player?.name ?? "Onbekend",
            position: p.start_position ?? p.position ?? "—",
            shirtNumber: p.shirt_number ?? 0,
            playerId: p.player?.id ?? "",
        }));

    const homeSide = match?.sides?.find((s) => s.side === "HOME") as Side | undefined;
    const awaySide = match?.sides?.find((s) => s.side === "AWAY") as Side | undefined;

    const playersHome: FormationPlayer[] = toFormationPlayers(homeSide?.players?.map(p => ({ ...p, side_id: homeSide?.id ?? "" })));
    const playersAway: FormationPlayer[] = toFormationPlayers(awaySide?.players?.map(p => ({ ...p, side_id: awaySide?.id ?? "" })));

    const handlePlayerClick = (p: PlacedPlayer) => {
        setSelectedPlayer(p);
    };

    return (
        <>
            <MatchHeader
                dateLabel={formatDate(match?.scheduled_at, { format: "dd/MM/yyyy" })}
                timeLabel={formatDate(match?.scheduled_at, { format: "HH:mm" })}
                competition={match?.competitionName ?? "-"}
                home={{
                    id: match?.homeClub?.id ?? "",
                    name: match?.homeClub?.name ?? "Home",
                    badgeUrl: match?.homeClub?.imageUrl ?? "",
                    formation: homeSide?.starting_formation ?? "-",
                    score: match?.homeScore ?? 0,
                }}
                away={{
                    id: match?.awayClub?.id ?? "",
                    name: match?.awayClub?.name ?? "Away",
                    badgeUrl: match?.awayClub?.imageUrl ?? "",
                    formation: awaySide?.starting_formation ?? "-",
                    score: match?.awayScore ?? 0,
                }}
            />
            <Tabs className="mt-4" value={tab} onValueChange={setTab}>
                <TabsList className="justify-start ml-4">
                    <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                    <TabsTrigger value="formations">Formaties</TabsTrigger>
                </TabsList>


                <TabsContent value="dashboard">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                        <div>
                        <Card>
                            <CardHeader>
                                <CardTitle>Thuis starters</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {homeSide?.players?.length ? (
                                    <PlayersTable competition={competition} players={toPlayersTableRows(homeSide.players.filter(p => p.is_starter))} />
                                ) : (
                                    <div className="text-sm text-muted-foreground italic">Geen spelers gevonden</div>
                                )}
                            </CardContent>
                            <CardHeader>
                            <CardTitle>Wissels</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {homeSide?.players?.length ? (
                                    <PlayersTable competition={competition} players={toPlayersTableRows(homeSide.players.filter(p => !p.is_starter))} />
                                ) : (
                                    <div className="text-sm text-muted-foreground italic">Geen spelers gevonden</div>
                                )}
                            </CardContent>
                        </Card>
                        </div>
                        <div>
                            <Card>
                                <CardHeader>
                                    <CardTitle>Uit starters</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {awaySide?.players?.length ? (
                                        <PlayersTable competition={competition} players={toPlayersTableRows(awaySide.players.filter(p => p.is_starter))} />
                                    ) : (
                                        <div className="text-sm text-muted-foreground italic">Geen spelers gevonden</div>
                                    )}
                                </CardContent>

                                <CardHeader>
                                    <CardTitle>Wissels</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {awaySide?.players?.length ? (
                                        <PlayersTable competition={competition} players={toPlayersTableRows(awaySide.players.filter(p => !p.is_starter))} />
                                    ) : (
                                        <div className="text-sm text-muted-foreground italic">Geen spelers gevonden</div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>
                <TabsContent value="formations" className="flex max-h-[800px] justify-between w-full ">
                <div className="m-4 flex justify-center">
                    <FormationGrid
                    formationHome={homeSide?.starting_formation ?? ""}
                    formationAway={awaySide?.starting_formation ?? ""}
                    playersHome={playersHome}
                    playersAway={playersAway}
                    onPlayerClick={handlePlayerClick}
                    />
                </div>

                <PlayerFormationCard Player={selectedPlayer}/>
                </TabsContent>
            </Tabs>
        </>
    )
}
export default MatchDetailPage
